import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Check, Lock, Trash2 } from 'lucide-react'
import {
  adminDeleteFlag,
  adminListFlags,
  adminLogin,
  adminResolveFlag,
  type FlagRecord,
} from '@/lib/admin/adminApi'

const SESSION_KEY = 'rr-admin-token'

interface DetailedFlag extends FlagRecord {
  details: Record<string, unknown> | null
}

export default function AdminFlagsPage() {
  const [token, setToken] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY)
    } catch {
      return null
    }
  })
  const [password, setPassword] = useState('')
  const [authError, setAuthError] = useState<string | null>(null)
  const [flags, setFlags] = useState<DetailedFlag[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  const loadFlags = useCallback(async (t: string) => {
    setLoading(true)
    try {
      const data = await adminListFlags(t)
      setFlags(data.map((f) => ({ ...f, details: null })))
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (token) void loadFlags(token)
  }, [token, loadFlags])

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setAuthError(null)
    try {
      const { token: t } = await adminLogin(password)
      setToken(t)
      try {
        sessionStorage.setItem(SESSION_KEY, t)
      } catch {
        /* ignore */
      }
      setPassword('')
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : 'Login failed')
    }
  }

  const resolve = async (id: string) => {
    if (!token) return
    setBusyId(id)
    try {
      await adminResolveFlag(token, id, true)
      setFlags((f) => f.map((x) => (x.id === id ? { ...x, status: 'resolved' } : x)))
    } catch (err) {
      console.error(err)
    } finally {
      setBusyId(null)
    }
  }

  const reopen = async (id: string) => {
    if (!token) return
    setBusyId(id)
    try {
      await adminResolveFlag(token, id, false)
      setFlags((f) => f.map((x) => (x.id === id ? { ...x, status: 'open' } : x)))
    } catch (err) {
      console.error(err)
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (id: string) => {
    if (!token) return
    setBusyId(id)
    try {
      await adminDeleteFlag(token, id)
      setFlags((f) => f.filter((x) => x.id !== id))
    } catch (err) {
      console.error(err)
    } finally {
      setBusyId(null)
    }
  }

  const signOut = () => {
    setToken(null)
    try {
      sessionStorage.removeItem(SESSION_KEY)
    } catch {
      /* ignore */
    }
  }

  const open = flags.filter((f) => f.status !== 'resolved')
  const resolved = flags.filter((f) => f.status === 'resolved')

  return (
    <div className="min-h-[100dvh] bg-[#0A0A0B] text-white">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white mb-8"
        >
          <ArrowLeft size={16} /> Back to map
        </Link>

        <h1 className="text-2xl font-bold tracking-tight mb-1">Flag review</h1>
        <p className="text-sm text-white/40 mb-8">
          Rider-submitted road reports for the RoadRatings team.
        </p>

        {!token ? (
          <form
            onSubmit={signIn}
            className="mx-auto max-w-sm space-y-4 rounded-xl border border-white/10 bg-white/[0.03] p-6"
          >
            <div className="flex items-center gap-2 text-white/60">
              <Lock size={16} />
              <span className="text-sm">Reviewer sign-in</span>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              className="h-11 w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-amber-400/60"
            />
            {authError && <p className="text-sm text-red-400">{authError}</p>}
            <button
              type="submit"
              className="h-11 w-full rounded-lg bg-amber-400 text-black text-sm font-semibold hover:bg-amber-300 transition-colors"
            >
              Sign in
            </button>
          </form>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="text-xs font-mono uppercase tracking-widest text-white/40">
                {open.length} open · {resolved.length} resolved
              </div>
              <button
                onClick={signOut}
                className="min-h-11 px-3 rounded-lg border border-white/10 text-xs text-white/50 hover:text-white"
              >
                Sign out
              </button>
            </div>

            {loading && <p className="text-sm text-white/40">Loading flags…</p>}
            {!loading && flags.length === 0 && (
              <p className="text-sm text-white/40">No flags submitted yet.</p>
            )}

            <div className="space-y-3">
              {open.map((f) => (
                <FlagCard
                  key={f.id}
                  flag={f}
                  busy={busyId === f.id}
                  onResolve={() => resolve(f.id)}
                  onDelete={() => remove(f.id)}
                />
              ))}
              {resolved.length > 0 && (
                <div className="pt-6">
                  <div className="text-xs font-mono uppercase tracking-widest text-white/30 mb-3">
                    Resolved
                  </div>
                  <div className="space-y-3 opacity-60">
                    {resolved.map((f) => (
                      <FlagCard
                        key={f.id}
                        flag={f}
                        busy={busyId === f.id}
                        resolved
                        onReopen={() => reopen(f.id)}
                        onDelete={() => remove(f.id)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function FlagCard({
  flag,
  busy,
  resolved = false,
  onResolve,
  onReopen,
  onDelete,
}: {
  flag: DetailedFlag
  busy: boolean
  resolved?: boolean
  onResolve?: () => void
  onReopen?: () => void
  onDelete: () => void
}) {
  const date = new Date(flag.created_at)
  const dateStr = isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">
            {flag.road_name ?? 'Unknown road'}
          </div>
          <div className="text-xs text-white/40 mt-0.5">
            {dateStr}
            {flag.road_id ? ` · ${flag.road_id}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {resolved ? (
            <button
              onClick={onReopen}
              disabled={busy}
              className="min-h-9 px-2 rounded-lg border border-white/10 text-xs text-white/50 hover:text-white disabled:opacity-40"
            >
              Reopen
            </button>
          ) : (
            <button
              onClick={onResolve}
              disabled={busy}
              title="Mark resolved"
              aria-label="Mark resolved"
              className="h-9 w-9 flex items-center justify-center rounded-lg bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-40"
            >
              <Check size={16} />
            </button>
          )}
          <button
            onClick={onDelete}
            disabled={busy}
            title="Delete flag"
            aria-label="Delete flag"
            className="h-9 w-9 flex items-center justify-center rounded-lg bg-red-500/10 border border-red-400/30 text-red-300 hover:bg-red-500/20 disabled:opacity-40"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      <p className="mt-3 text-sm text-white/75 whitespace-pre-wrap">{flag.reason}</p>
    </div>
  )
}
